import { ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import NervenetEmbedTokenAPI from 'dashboard/api/nervenetEmbedToken';

// The embed token is cached at module scope (not per-composable-instance)
// and reused across every conversation the agent opens in this tab session
// — minting a fresh 4-hour token on every conversation switch would be one
// more round trip than the badge actually needs. Re-minted lazily on the
// first 401/403 the badge endpoint returns, in case it expired mid-session.
let cachedTokenPromise = null;

function embedToken() {
  if (!cachedTokenPromise) {
    cachedTokenPromise = NervenetEmbedTokenAPI.create().then(
      response => response.data.token
    );
  }
  return cachedTokenPromise;
}

function resetCachedToken() {
  cachedTokenPromise = null;
}

/**
 * Fetches the "(N)" badge count for the conversation bill tab — how many
 * bills (draft + issued) this conversation has, from Nervenet's badge/
 * freshness endpoint (Api::V1::Commerce::ConversationBillsController#count).
 * Same cw JWT the embedded panel iframe uses, not a mobile session header.
 */
export function useConversationBillBadge() {
  const { accountId } = useAccount();
  const count = ref(0);
  const enabled = ref(true);
  const isLoading = ref(false);

  const fetchCount = async (
    conversationDisplayId,
    { retrying = false } = {}
  ) => {
    if (!conversationDisplayId) return;

    isLoading.value = true;
    try {
      const token = await embedToken();
      const url =
        `${window.chatwootConfig.nervenetBaseUrl}/api/v1/commerce/accounts/` +
        `${accountId.value}/conversations/${conversationDisplayId}/count?cw=${token}`;

      const response = await fetch(url, { credentials: 'omit' });

      if (!response.ok) {
        if (response.status === 401 || (response.status === 404 && !retrying)) {
          resetCachedToken();
          if (!retrying) {
            await fetchCount(conversationDisplayId, { retrying: true });
          }
        }
        return;
      }

      const data = await response.json();
      count.value = data.count || 0;
      enabled.value = data.enabled !== false;
    } catch (error) {
      // A missed badge fetch is not worth surfacing to the agent — the tab
      // just shows no count this time, same as if the endpoint were slow.
    } finally {
      isLoading.value = false;
    }
  };

  return { count, enabled, isLoading, fetchCount };
}
